import { animated, useSpring } from '@react-spring/web';
import MagazineTag from '@uikit/components/MagazineTag/MagazineTag';
import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import useIsMobile from '../../../../hooks/useIsMobile/useIsMobile';
import useMeasure from '../../../../hooks/useMeasure/useMeasure';
import Icon from '../../Icon/Icon';
import type { MagazineTagNavigationProps } from './interfaces';
import styles from './MagazineTagNavigation.module.scss';

const MagazineTagNavigation = ({
  tags,
  toggleSelectedTag = () => void 0,
  isSelectedTag = () => false,
}: MagazineTagNavigationProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isSmallMobile = useIsMobile(375);

  const isMobile = useIsMobile(768);

  const heightRef = useRef<HTMLElement>();
  const { height } = useMeasure(heightRef);

  const heightAnimProps = useSpring({
    height: isMenuOpen || !isMobile ? height : 0,
    config: {
      mass: 1,
      tension: 100,
      friction: 16,
      clamp: height === 0,
    },
    delay: 100,
  });

  const toggleMenu = () => {
    setIsMenuOpen((value) => !value);
  };

  const listBoxAriaLabelProps = isMobile
    ? {
        'aria-labelledby': 'listbox-categories-label',
      }
    : {
        'aria-label': 'Welche Themen interessieren Sie?',
      };

  return (
    <div className={styles.base}>
      <div className={styles.headline}>Welche Themen interessieren Sie?</div>
      <div
        className={clsx(styles.taglist, {
          [styles.open]: isMenuOpen && isMobile,
        })}
      >
        {isMobile && (
          <div
            className={clsx(styles.tag, styles.mobilePlaceholder)}
            onClick={toggleMenu}
          >
            <span id="listbox-categories-label">
              {isSmallMobile
                ? 'Themen anzeigen'
                : 'Welche Themen interessieren Sie?'}
            </span>

            <div
              className={clsx(styles.buttonDropdown, {
                [styles.open]: isMenuOpen,
              })}
            >
              <Icon variant={'action/chevron-down'} size="iconSize16" />
            </div>
          </div>
        )}

        <animated.div
          className={styles.heightAnimator}
          style={isMobile ? heightAnimProps : {}}
        >
          <div
            id="listbox"
            tabIndex={0}
            role="listbox"
            aria-multiselectable={true}
            {...listBoxAriaLabelProps}
            className={styles.animatorRef}
            ref={heightRef as React.MutableRefObject<HTMLDivElement>}
          >
            {tags.map((tag) =>
              isMobile ? (
                <button
                  role="option"
                  aria-selected={isSelectedTag(tag)}
                  onClick={() => toggleSelectedTag(tag)}
                  className={clsx(styles.tag, {
                    [styles.active]: isSelectedTag(tag),
                    [styles.closed]: !isMenuOpen && isMobile,
                  })}
                  key={tag}
                >
                  <Icon
                    className={styles.iconCheckmark}
                    variant={'action/checkmark'}
                    size="iconSize20"
                  />
                  {tag}
                </button>
              ) : (
                <MagazineTag
                  asButton
                  variant="outlined"
                  role="option"
                  isHighlighted={isSelectedTag(tag)}
                  aria-selected={isSelectedTag(tag)}
                  onClick={() => toggleSelectedTag(tag)}
                  key={tag}
                  title={tag}
                />
              )
            )}
          </div>
        </animated.div>
      </div>
    </div>
  );
};

export default MagazineTagNavigation;
